import * as THREE from 'three';
import { GAME_CONFIG } from '../config/gameConfig';
import type { Field } from '../state/types';

const EMISSIVE_IDLE = 0x000000;
const EMISSIVE_HOVER = 0x3d5a1a;
const EMISSIVE_HOVER_DEAD = 0x2a2a2a;
const EMISSIVE_SELECTED = 0x4a7a22;
const INTENSITY_IDLE = 0;
const INTENSITY_HOVER = 0.35;
const INTENSITY_HOVER_DEAD = 0.08;
const INTENSITY_SELECTED = 0.55;

const CROP_HEALTHY = new THREE.Color(0x5a9e28);
const CROP_STRESSED = new THREE.Color(0xd4b03a);
const CROP_INFECTED = new THREE.Color(0x8a5a28);
const CROP_DEAD = new THREE.Color(0x4a4a4a);

const SOIL_COLOR = 0x3a2818;
const BORDER_COLOR = 0x5c4030;

/** Dense diagonal furrows — reads like the concept-art crop surface. */
const GRID_SEGS = 56;
const RIDGE_COUNT = 22;
const RIDGE_HEIGHT = 0.14;
const CROP_INSET = 0.18;
const BORDER_WIDTH = 0.14;
const BORDER_HEIGHT = 0.12;

const NDVI_TEX_SIZE = 96;
/** Float clearly above crop ridges so NDVI reads as a separate survey layer. */
const NDVI_PLANE_Y = 1.45;
const NDVI_FRESH_OPACITY = 0.92;

/** Deterministic 0..1 hash for mottling (stable per field vertex). */
function hash2(x: number, z: number, seed: number): number {
  const n = Math.sin(x * 127.1 + z * 311.7 + seed * 74.7) * 43758.5453;
  return n - Math.floor(n);
}

/**
 * Square crop surface with diagonal convex ridges.
 * Vertex colors store relative shade (valleys darker, slight mottling);
 * material.color carries the infection tint.
 */
function createCorrugatedCropGeometry(size: number, seed: number): THREE.BufferGeometry {
  const geo = new THREE.PlaneGeometry(size, size, GRID_SEGS, GRID_SEGS);
  geo.rotateX(-Math.PI / 2);

  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const freq = (RIDGE_COUNT * Math.PI) / size;

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);

    // Diagonal rows (concept art furrow direction)
    const u = (x + z) * freq;
    const wave = 0.5 + 0.5 * Math.sin(u);
    // Rounded convex mounds (power < 1 widens the ridge tops)
    const ridge = Math.pow(wave, 0.55);
    pos.setY(i, ridge * RIDGE_HEIGHT);

    // Valley → dark soil gap; crest → brighter crop; soft blotches
    const mottling = 0.9 + 0.12 * hash2(x * 3.1, z * 3.1, seed);
    const shade = (0.55 + ridge * 0.45) * mottling;
    colors[i * 3] = shade;
    colors[i * 3 + 1] = shade;
    colors[i * 3 + 2] = shade;
  }

  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  return geo;
}

function addFieldBorder(group: THREE.Group, size: number, fieldId: number): void {
  const mat = new THREE.MeshStandardMaterial({
    color: BORDER_COLOR,
    roughness: 0.92,
  });
  const half = size * 0.5;
  const y = 0.2 + BORDER_HEIGHT * 0.5;
  const long = size + BORDER_WIDTH;

  const specs: Array<[number, number, number, number, number]> = [
    // [w, h, d, x, z]
    [long, BORDER_HEIGHT, BORDER_WIDTH, 0, -half],
    [long, BORDER_HEIGHT, BORDER_WIDTH, 0, half],
    [BORDER_WIDTH, BORDER_HEIGHT, size, -half, 0],
    [BORDER_WIDTH, BORDER_HEIGHT, size, half, 0],
  ];

  for (const [w, h, d, x, z] of specs) {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = { fieldId };
    group.add(mesh);
  }
}

function infectionToCropColor(
  infection: number,
  dead: boolean | undefined,
  out: THREE.Color,
): THREE.Color {
  if (dead) {
    return out.copy(CROP_DEAD);
  }

  const t = Math.min(1, Math.max(0, infection / 100));
  if (t < 0.4) {
    return out.copy(CROP_HEALTHY).lerp(CROP_STRESSED, t / 0.4);
  }
  return out.copy(CROP_STRESSED).lerp(CROP_INFECTED, (t - 0.4) / 0.6);
}

/** Sync crop material tint from infection / dead state. */
export function updateCropVisuals(f: Field): void {
  if (!f.cropMat) return;
  infectionToCropColor(f.infection, f.dead, f.cropMat.color);
}

/** Smoothstep for bilinear value-noise interpolation. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Coarse-grid value noise — neighbors share similar values → contiguous blobs.
 * `cells` ≈ number of patches across the field (keep small: 3–5).
 */
function valueNoise2D(x: number, y: number, cells: number, seed: number): number {
  const gx = (x / NDVI_TEX_SIZE) * cells;
  const gy = (y / NDVI_TEX_SIZE) * cells;
  const x0 = Math.floor(gx);
  const y0 = Math.floor(gy);
  const fx = smoothstep(gx - x0);
  const fy = smoothstep(gy - y0);

  const v00 = hash2(x0, y0, seed);
  const v10 = hash2(x0 + 1, y0, seed);
  const v01 = hash2(x0, y0 + 1, seed);
  const v11 = hash2(x0 + 1, y0 + 1, seed);

  const a = v00 + (v10 - v00) * fx;
  const b = v01 + (v11 - v01) * fx;
  return a + (b - a) * fy;
}

/**
 * Classic NDVI false-color ramp (healthy → stressed):
 * green → yellow-green → yellow → orange → red.
 */
function stressToNdviRgb(stress: number): [number, number, number] {
  const t = Math.min(1, Math.max(0, stress));
  const stops: Array<[number, number, number, number]> = [
    [0.0, 34, 180, 60],
    [0.25, 120, 200, 40],
    [0.45, 230, 210, 40],
    [0.65, 240, 140, 30],
    [0.85, 230, 70, 40],
    [1.0, 200, 30, 30],
  ];

  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, r0, g0, b0] = stops[i];
    const [t1, r1, g1, b1] = stops[i + 1];
    if (t <= t1 || i === stops.length - 2) {
      const u = t1 === t0 ? 0 : Math.min(1, Math.max(0, (t - t0) / (t1 - t0)));
      return [
        Math.round(r0 + (r1 - r0) * u),
        Math.round(g0 + (g1 - g0) * u),
        Math.round(b0 + (b1 - b0) * u),
      ];
    }
  }
  return [200, 30, 30];
}

/**
 * Large contiguous NDVI regions (green / yellow / orange / red).
 * Higher infection shifts the field toward more stress colors.
 */
function generateNdviTexture(infection: number, seed: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = NDVI_TEX_SIZE;
  canvas.height = NDVI_TEX_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return new THREE.CanvasTexture(canvas);
  }

  const img = ctx.createImageData(NDVI_TEX_SIZE, NDVI_TEX_SIZE);
  const data = img.data;
  const infectionBias = (infection / 100) * 0.45;

  for (let y = 0; y < NDVI_TEX_SIZE; y++) {
    for (let x = 0; x < NDVI_TEX_SIZE; x++) {
      // Coarse blobs stay contiguous; light mid-scale warp for natural edges
      const base = valueNoise2D(x, y, 3.2, seed);
      const warp = valueNoise2D(x, y, 5.5, seed + 7) * 0.2;
      let stress = base * 0.8 + warp + infectionBias - 0.08;
      stress = Math.min(1, Math.max(0, stress));

      const [r, g, b] = stressToNdviRgb(stress);
      const i = (y * NDVI_TEX_SIZE + x) * 4;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

function disposeNdviMap(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>): void {
  const map = mesh.material.map;
  if (map) {
    map.dispose();
    mesh.material.map = null;
  }
}

/** Apply a new NDVI snapshot after a successful survey scan. */
export function applyNdviScan(f: Field): void {
  if (!f.ndviMesh) return;

  disposeNdviMap(f.ndviMesh);
  const seed = Math.random() * 1000 + f.id * 17;
  const tex = generateNdviTexture(f.infection, seed);
  f.ndviMesh.material.map = tex;
  f.ndviMesh.material.needsUpdate = true;
  f.ndviMesh.visible = true;
  f.ndviMesh.material.opacity = NDVI_FRESH_OPACITY;
}

/** Sync NDVI plane opacity / visibility from remaining lifetime. */
export function updateNdviVisuals(f: Field): void {
  if (!f.ndviMesh) return;

  if (f.ndviRemaining <= 0 || f.dead) {
    f.ndviMesh.visible = false;
    f.ndviMesh.material.opacity = 0;
    return;
  }

  const fadeSec = GAME_CONFIG.fields.ndviFadeSec;
  const opacity =
    f.ndviRemaining > fadeSec
      ? NDVI_FRESH_OPACITY
      : NDVI_FRESH_OPACITY * (f.ndviRemaining / fadeSec);

  f.ndviMesh.visible = opacity > 0.01;
  f.ndviMesh.material.opacity = opacity;
}

/** Clear NDVI overlay (reset / death). */
export function clearNdviOverlay(f: Field): void {
  f.ndviRemaining = 0;
  if (!f.ndviMesh) return;
  disposeNdviMap(f.ndviMesh);
  f.ndviMesh.visible = false;
  f.ndviMesh.material.opacity = 0;
}

function applyHighlight(field: Field, emissive: number, intensity: number): void {
  if (!field.soilMat) return;
  field.soilMat.emissive.setHex(emissive);
  field.soilMat.emissiveIntensity = intensity;
}

function clearHighlight(fields: Field[]): void {
  for (const f of fields) {
    if (!f.soilMat) continue;
    f.soilMat.emissive.setHex(EMISSIVE_IDLE);
    f.soilMat.emissiveIntensity = INTENSITY_IDLE;
  }
}

/** Refresh soil emissive from hover + selected ids (selected wins over hover). */
export function updateFieldHighlights(
  fields: Field[],
  hoveredFieldId: number | null,
  selectedFieldId: number | null,
): void {
  clearHighlight(fields);

  if (hoveredFieldId !== null && hoveredFieldId !== selectedFieldId) {
    const hovered = fields.find((f) => f.id === hoveredFieldId);
    if (hovered) {
      if (hovered.dead) {
        applyHighlight(hovered, EMISSIVE_HOVER_DEAD, INTENSITY_HOVER_DEAD);
      } else {
        applyHighlight(hovered, EMISSIVE_HOVER, INTENSITY_HOVER);
      }
    }
  }

  if (selectedFieldId !== null) {
    const selected = fields.find((f) => f.id === selectedFieldId);
    if (selected && !selected.dead) {
      applyHighlight(selected, EMISSIVE_SELECTED, INTENSITY_SELECTED);
    }
  }
}

export function buildFieldMeshes(scene: THREE.Scene, fields: Field[]): void {
  fields.forEach((f) => {
    const fieldGroup = new THREE.Group();
    fieldGroup.position.set(f.x, 0, f.z);
    fieldGroup.userData = { fieldId: f.id };

    const soilGeo = new THREE.BoxGeometry(f.size, 0.2, f.size);
    const soilMat = new THREE.MeshStandardMaterial({
      color: SOIL_COLOR,
      roughness: 0.95,
      emissive: EMISSIVE_IDLE,
      emissiveIntensity: INTENSITY_IDLE,
    });
    const soil = new THREE.Mesh(soilGeo, soilMat);
    soil.position.y = 0.1;
    soil.receiveShadow = true;
    soil.castShadow = true;
    soil.userData = { fieldId: f.id };
    fieldGroup.add(soil);
    f.mesh = soil;
    f.soilMat = soilMat;

    addFieldBorder(fieldGroup, f.size, f.id);

    const cropSize = f.size - CROP_INSET * 2;
    const cropGeo = createCorrugatedCropGeometry(cropSize, f.id * 17.3);
    const cropMat = new THREE.MeshStandardMaterial({
      color: CROP_HEALTHY.clone(),
      roughness: 0.9,
      vertexColors: true,
      flatShading: false,
    });
    f.cropMat = cropMat;
    updateCropVisuals(f);

    const crop = new THREE.Mesh(cropGeo, cropMat);
    crop.position.y = 0.2;
    crop.castShadow = true;
    crop.receiveShadow = true;
    crop.userData = { fieldId: f.id };
    fieldGroup.add(crop);

    const ndviGeo = new THREE.PlaneGeometry(f.size - CROP_INSET, f.size - CROP_INSET);
    ndviGeo.rotateX(-Math.PI / 2);
    const ndviMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ndvi = new THREE.Mesh(ndviGeo, ndviMat);
    ndvi.position.y = NDVI_PLANE_Y;
    ndvi.visible = false;
    ndvi.renderOrder = 1;
    ndvi.userData = { fieldId: f.id };
    fieldGroup.add(ndvi);
    f.ndviMesh = ndvi;

    scene.add(fieldGroup);
  });
}
